import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Input, Modal, Typography } from 'antd'
import { PortalTableClipboardGrid, parsePortalTableClipboard } from '../../helpers/portal-table-clipboard'
import { PORTAL_TABLE_MAX_COLUMNS, PORTAL_TABLE_MAX_ROWS } from '../../helpers/portal-table'

interface Props {
  open: boolean
  hasCurrentContent: boolean
  onCancel: () => void
  onApply: (grid: PortalTableClipboardGrid) => void
}

export function PortalTablePasteModal({ open, hasCurrentContent, onCancel, onApply }: Props) {
  const [value, setValue] = useState('')
  const [error, setError] = useState<string>()

  useEffect(() => {
    if (!open) return
    setValue('')
    setError(undefined)
  }, [open])

  const dimensions = useMemo(() => {
    if (!value) return null
    const grid = parsePortalTableClipboard(value)
    return { columns: grid.headers.length, rows: grid.rows.length }
  }, [value])

  const applyGrid = () => {
    const grid = parsePortalTableClipboard(value)
    if (grid.headers.length === 0) {
      setError('Cole ao menos uma linha com cabeçalhos.')
      return
    }
    if (grid.headers.length > PORTAL_TABLE_MAX_COLUMNS || grid.rows.length > PORTAL_TABLE_MAX_ROWS) {
      setError(
        `Foram recebidas ${grid.headers.length} colunas e ${grid.rows.length} linhas. ` +
          `O limite é ${PORTAL_TABLE_MAX_COLUMNS} colunas e ${PORTAL_TABLE_MAX_ROWS} linhas.`
      )
      return
    }

    const commit = () => onApply(grid)
    if (!hasCurrentContent) {
      commit()
      return
    }
    Modal.confirm({
      title: 'Substituir a grade atual?',
      content: 'Cabeçalhos, linhas e células atuais serão substituídos pelos dados colados.',
      okText: 'Substituir',
      cancelText: 'Cancelar',
      onOk: commit,
    })
  }

  return (
    <Modal
      open={open}
      title="Colar planilha"
      okText="Usar dados colados"
      cancelText="Cancelar"
      onOk={applyGrid}
      onCancel={onCancel}
      width={760}
      destroyOnClose
    >
      <Typography.Paragraph type="secondary">
        Copie uma área do Excel ou Google Sheets e cole abaixo. A primeira linha será usada como
        cabeçalho e as seguintes como dados.
      </Typography.Paragraph>
      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} /> : null}
      <Input.TextArea
        autoFocus
        rows={12}
        value={value}
        placeholder={'ESTADO\tMÉDIA\tPISO\nSão Paulo\tR$ 3.786,00\tR$ 4.639,00'}
        onChange={(event) => {
          setValue(event.target.value)
          setError(undefined)
        }}
      />
      {dimensions ? (
        <Typography.Text type="secondary">
          Detectado: {dimensions.columns} coluna(s) e {dimensions.rows} linha(s) de dados.
        </Typography.Text>
      ) : null}
    </Modal>
  )
}
